import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { environmentURLs } from '../../../components/common/CaptureEnvironment';
import * as _ from 'lodash';
import { mpDpService } from '../../../services/mp-dp-service/service';

const initialState = {
    getConsentPending: true,
    consentDetails: {},
    getConsentsError: false,
    signedConsentDetails: {},
    signedUrlPendingLocations: [],
    getSignedS3ConsentsError: false
}

export const getConsents = createAsyncThunk(
    'getConsents',
    async ({ id, entityId, token }) => {
      let response = await axios.get(
        `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/solutions/${id}/consents?entityId=${entityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return {
        entityId,
        response
      };
    }
);

export const getSignedS3Consents = createAsyncThunk(
  'getSignedS3Url',
  async ({type, operation, expires, token, fileName, entityId}) => {
    let response = await mpDpService.s3.createSignedUrl({type, operation, expires, fileName, token});
    return {
      type,
      response,
      entityId
    };
  }
);

export const consentDetailsSlice = createSlice({
  name: 'consentDetailsSlice',
  initialState,
  reducers: {
    clearConsentDetails: (state) => {
      state.consentDetails = {};
      state.getConsentPending = true;
      state.getConsentsError = false;
      state.signedConsentDetails = {};
      state.getSignedS3ConsentsPendingLocations = [];
      state.getSignedS3ConsentsError = false;
    },
    updateSignedConsentDetails: (state, action) => {
      let entityId = action.payload.entityId;
      let signBy = action.payload.signBy;
      let document = action.payload.document;
      const consentDetails = state.consentDetails;
      if (signBy) {
        let signedConsentDetails;
        signedConsentDetails = _.merge(state.signedConsentDetails, {
          [entityId]: {
            signBy: signBy,
            documents: [],
            businessRoles: consentDetails && consentDetails[entityId] && consentDetails[entityId].businessRoles
          }
        });
        state.signedConsentDetails = signedConsentDetails;
      } else if (document) {
        const signedDetails = state.signedConsentDetails;
        let documents = signedDetails && signedDetails[entityId] && signedDetails[entityId].documents;
        if (!documents.includes(document)) {
          let signedConsentDetails;
          documents.push(document);
          signedConsentDetails = _.merge(state.signedConsentDetails, {
            [entityId]: {
              documents: documents,
              businessRoles: consentDetails && consentDetails[entityId] && consentDetails[entityId].businessRoles
            }
          });
          state.signedConsentDetails = signedConsentDetails;
        }
      }
    }
  },
  extraReducers: {
    [getConsents.fulfilled]: (state, action) => {
      state.getConsentPending = false;
      state.getConsentsError = false;
      let consents =
        action.payload &&
        action.payload.response &&
        action.payload.response.data;
      let entityId = action.payload && action.payload.entityId;
      const signedBy = (state.consentDetails[entityId] && state.consentDetails[entityId].signBy) || [];
      const businessRoles = (state.consentDetails[entityId] && state.consentDetails[entityId].businessTitle) || [];
      const documents = (state.consentDetails[entityId] && state.consentDetails[entityId].documents) || [];
      Object.values(consents).map(consent => {
        let businessTitle = consent.businessTitle && consent.businessTitle.toUpperCase()
        if (!signedBy.length || !signedBy.includes(consent.name)) {
          signedBy.push(consent.name)
        }
        if (businessTitle && (!businessRoles.length || !businessRoles.includes(businessTitle))) {
          businessRoles.push(businessTitle)
        }
        if (consent.consentType === 'solution_tnc') {
          if (consent.signedTermsAndConditions) {
            documents.push({signedTerms: consent.signedTermsAndConditions});
          } else {
            documents.push({appTerms: consent.documentLink});
          }
        } else if (consent.consentType === 'api_data'){
          if (consent.customConsentForm && !documents.some(custom => custom.customConsent === consent.customConsentForm)) {
            documents.push({customConsent: consent.customConsentForm});
          } else if (!consent.customConsentForm && !consent.signedTermsAndConditions && consent.documentLink) {
            documents.push({apiTerms: consent.documentLink});
          } else if (!consent.customConsentForm && consent.signedTermsAndConditions) {
            documents.push({signedTerms: consent.signedTermsAndConditions});
          }
        } else if (consent.consentType === 'payments' && consent.documentLink) {
          documents.push({payments: consent.documentLink});
        } else if (consent.consentType === 'data_rights' && consent.signedTermsAndConditions) {
          documents.push({dataRights: consent.signedTermsAndConditions});
        }
      });

      let consentDetails;
      consentDetails = _.merge(state.consentDetails, {
        [entityId]: {
          signBy: signedBy,
          documents: documents,
          businessRoles: businessRoles
        }
      });
      state.consentDetails = consentDetails;
    },
    [getConsents.pending]: state => {
      state.getConsentPending = true;
      state.getConsentsError = false;
    },
    [getConsents.rejected]: state => {
      state.getConsentPending = false;
      state.getConsentsError = true;
    },
    [getSignedS3Consents.fulfilled]: (state, action) => {
      const signedDocumentLink = action.payload.response.data.signedRequest;
      const entityId = action.payload.entityId;
      const signedDetails = state.signedConsentDetails;
      let documents = signedDetails && signedDetails[entityId] && signedDetails[entityId].documents;
      if(documents && !documents.includes(signedDocumentLink)){
        documents.push(signedDocumentLink);
        let signedConsentDetails;
        signedConsentDetails = _.merge(state.signedConsentDetails, {
          [entityId]: {
            documents: documents
          }
        });
        state.signedConsentDetails = signedConsentDetails;
      }
      state.signedUrlPendingLocations = state.signedUrlPendingLocations.filter(loc => loc !== entityId);
      state.getSignedS3ConsentsError = false;
    },
    [getSignedS3Consents.pending]: (state, action) => {
      state.signedUrlPendingLocations.push(action.meta.arg.entityId);
      state.getSignedS3ConsentsError = false;
    },
    [getSignedS3Consents.rejected]: (state, action) => {
      const entityId = action.meta.arg.entityId;
      state.signedUrlPendingLocations = state.signedUrlPendingLocations.filter(loc => loc !== entityId);
      state.getSignedS3ConsentsError = true;
    },
  }
});

export const { clearConsentDetails, updateSignedConsentDetails } = consentDetailsSlice.actions;

export default consentDetailsSlice.reducer;
